import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { marketplaceAPI, categoriesAPI, settingsAPI } from '../lib/api';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { Search, ShoppingCart, Menu, X, Star, ChevronRight, Sparkles, TrendingUp, Store, Play, MapPin, Share2, ShoppingBag, Users, Award, ThumbsUp, Home, Truck } from 'lucide-react';
import { SiteFooter } from '../components/SiteFooter';
import { AIAssistant } from '../components/AIAssistant';
import { StoreProductCarousel } from '../components/StoreProductCarousel';
import { DynamicShowcase } from '../components/DynamicShowcase';
import { TopRatedSection } from '../components/TopRatedSection';
import { RatingBadge, useBulkRatings } from '../components/RatingBadge';
import { optimizeCloudinaryUrl } from '../lib/cloudinary';

// Default categories with colors
const DEFAULT_CATEGORIES = [
  { id: 'moda-praia', name: 'Moda Praia', icon: '🏖️', color: '#22c55e' },
  { id: 'feminino', name: 'Feminino', icon: '👗', color: '#ec4899' },
  { id: 'masculino', name: 'Masculino', icon: '👔', color: '#3b82f6' },
  { id: 'infantil', name: 'Infantil', icon: '👶', color: '#f97316' },
  { id: 'calcados', name: 'Calçados', icon: '👟', color: '#06b6d4' },
  { id: 'acessorios', name: 'Acessórios', icon: '💍', color: '#eab308' },
  { id: 'eletronicos', name: 'Eletrônicos', icon: '📱', color: '#6b7280' },
  { id: 'casa', name: 'Casa', icon: '🏠', color: '#f97316' },
];

export const MarketplaceHome = () => {
  const [data, setData] = useState(null);
  const [stats, setStats] = useState(null);
  const [storesFeed, setStoresFeed] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [footerCfg, setFooterCfg] = useState(null);
  const { itemCount } = useCart();
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
    settingsAPI.getFooter().then(({ data }) => setFooterCfg(data)).catch(() => {});
    marketplaceAPI.getStats().then(({ data }) => setStats(data)).catch(() => {});
    marketplaceAPI
      .getStoresWithProducts(20, 12)
      .then(({ data }) => setStoresFeed(data.stores || []))
      .catch(() => {});
  }, []);

  const loadData = async () => {
    try {
      const [homeRes, catRes] = await Promise.all([
        marketplaceAPI.getHome(),
        categoriesAPI.list()
      ]);
      setData(homeRes.data);
      setCategories(catRes.data.length > 0 ? catRes.data : DEFAULT_CATEGORIES);
    } catch (error) {
      console.error('Error loading data:', error);
      setCategories(DEFAULT_CATEGORIES);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/busca?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(price);
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20" data-testid="marketplace-home">
      {/* Header */}
      <header className="marketplace-header text-white">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            {footerCfg?.header_logo_horizontal_url ? (
              <img
                src={optimizeCloudinaryUrl(footerCfg.header_logo_horizontal_url, { width: 400 })}
                alt={footerCfg.company_name || 'DESTACK VENDA MAIS'}
                className="h-11 w-auto max-w-[220px] object-contain drop-shadow-lg"
                data-testid="header-logo-horizontal"
              />
            ) : footerCfg?.header_logo_url ? (
              <>
                <img
                  src={optimizeCloudinaryUrl(footerCfg.header_logo_url, { width: 120 })}
                  alt={footerCfg.company_name || 'DESTACK'}
                  className="w-11 h-11 rounded-xl object-cover bg-white shadow-lg ring-2 ring-amber-300/60"
                  data-testid="header-logo"
                />
                <div className="leading-tight">
                  <h1 className="text-lg font-extrabold tracking-tight text-white drop-shadow whitespace-nowrap" data-testid="header-brand-name">DESTACK</h1>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-amber-300">VENDA MAIS</p>
                </div>
              </>
            ) : (
              <>
                <div
                  className="w-11 h-11 rounded-xl flex items-center justify-center shadow-lg ring-2 ring-amber-300/60"
                  style={{ background: 'linear-gradient(135deg, #ffffff 0%, #fef3c7 100%)' }}
                  data-testid="header-logo-fallback"
                >
                  <span className="text-blue-700 font-extrabold text-xl tracking-tight">D</span>
                </div>
                <div className="leading-tight">
                  <h1 className="text-lg font-extrabold tracking-tight text-white drop-shadow" data-testid="header-brand-name">DESTACK</h1>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-amber-300">VENDA MAIS</p>
                </div>
              </>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button 
              onClick={() => {
                if (navigator.share) {
                  navigator.share({
                    title: 'DESTACK VENDAS MAIS',
                    text: 'Confira as melhores ofertas no DESTACK VENDAS MAIS!',
                    url: window.location.href
                  });
                } else {
                  navigator.clipboard.writeText(window.location.href);
                  alert('Link copiado!');
                }
              }}
              className="p-2"
              data-testid="share-btn"
            >
              <Share2 className="w-6 h-6" />
            </button>
            <Link to="/carrinho" className="relative p-2" data-testid="cart-btn">
              <ShoppingCart className="w-6 h-6" />
              {itemCount > 0 && (
                <span className="cart-badge">{itemCount}</span>
              )}
            </Link>
            <button onClick={() => setMenuOpen(true)} className="p-2" data-testid="menu-btn">
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="px-4 pb-4">
          <form onSubmit={handleSearch} className="search-bar">
            <Search className="w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="O que você está procurando?..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="search-input"
            />
            <button type="submit" className="search-btn" data-testid="search-submit">
              <Search className="w-5 h-5" />
            </button>
          </form>
        </div>
      </header>

      {/* Institutional Hero Banner */}
      <div className="px-4 py-4">
        <div
          className="relative overflow-hidden rounded-2xl p-6 sm:p-8 lg:p-14 text-white shadow-xl"
          style={{
            background: footerCfg?.hero_image_url
              ? `linear-gradient(135deg, rgba(30,58,138,0.85), rgba(37,99,235,0.75)), url(${optimizeCloudinaryUrl(footerCfg.hero_image_url, { width: 1920 })}) center/cover`
              : 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 55%, #1e40af 100%)',
          }}
          data-testid="institutional-hero"
        >
          <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-amber-400/20 blur-3xl" />
          <div className="absolute -left-10 -bottom-10 w-40 h-40 rounded-full bg-white/10 blur-3xl" />
          <div className="relative grid lg:grid-cols-2 gap-8 items-center">
            <div>
              {footerCfg?.hero_tagline && (
                <p
                  className="text-sm sm:text-base font-bold uppercase tracking-[0.35em] drop-shadow-lg"
                  style={{
                    background: 'linear-gradient(90deg, #fde047 0%, #facc15 50%, #fbbf24 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                  data-testid="hero-tagline"
                >
                  {footerCfg.hero_tagline}
                </p>
              )}
              <h2 className="text-3xl sm:text-5xl lg:text-6xl font-extrabold my-3 tracking-tight leading-[1.05] drop-shadow-md" data-testid="hero-title">
                {footerCfg?.hero_title || 'DESTACK VENDA MAIS'}
              </h2>
              <p className="text-base sm:text-lg lg:text-xl font-light text-white/95 mb-6 max-w-xl leading-relaxed" data-testid="hero-subtitle">
                {footerCfg?.hero_subtitle || 'O Marketplace Inteligente para Comprar e Vender'}
              </p>
              <Link
                to="/destaques"
                className="inline-flex items-center gap-2 bg-amber-400 text-slate-900 px-6 sm:px-8 py-3 rounded-full font-bold text-sm sm:text-base hover:bg-amber-300 transition-all shadow-lg hover:shadow-xl hover:scale-[1.02]"
                data-testid="hero-cta"
              >
                Explorar plataforma
                <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            {/* Right side KPIs / trust block - only on lg+ */}
            <div className="hidden lg:grid grid-cols-2 gap-4" data-testid="hero-kpis">
              <div className="bg-white/10 backdrop-blur border border-white/20 rounded-2xl p-5" data-testid="kpi-stores">
                <div className="flex items-center gap-2 text-amber-300 text-xs uppercase tracking-widest font-semibold">
                  <Store className="w-4 h-4" /> Lojas
                </div>
                <p className="text-3xl font-bold mt-2">{stats?.active_stores ?? '—'}</p>
                <p className="text-xs text-blue-100 mt-1">Parceiras ativas</p>
              </div>
              <div className="bg-white/10 backdrop-blur border border-white/20 rounded-2xl p-5" data-testid="kpi-products">
                <div className="flex items-center gap-2 text-amber-300 text-xs uppercase tracking-widest font-semibold">
                  <ShoppingBag className="w-4 h-4" /> Produtos
                </div>
                <p className="text-3xl font-bold mt-2">{stats?.total_products ?? '—'}</p>
                <p className="text-xs text-blue-100 mt-1">Cadastrados no marketplace</p>
              </div>
              <div className="bg-white/10 backdrop-blur border border-white/20 rounded-2xl p-5" data-testid="kpi-orders">
                <div className="flex items-center gap-2 text-amber-300 text-xs uppercase tracking-widest font-semibold">
                  <Award className="w-4 h-4" /> Pedidos
                </div>
                <p className="text-3xl font-bold mt-2">{stats?.completed_orders ?? '—'}</p>
                <p className="text-xs text-blue-100 mt-1">Concluídos com sucesso</p>
              </div>
              <div className="bg-white/10 backdrop-blur border border-white/20 rounded-2xl p-5" data-testid="kpi-customers">
                <div className="flex items-center gap-2 text-amber-300 text-xs uppercase tracking-widest font-semibold">
                  <Users className="w-4 h-4" /> Clientes
                </div>
                <p className="text-3xl font-bold mt-2">{stats?.unique_customers ?? '—'}</p>
                <p className="text-xs text-blue-100 mt-1">
                  {stats?.avg_rating ? (
                    <span className="inline-flex items-center gap-1">
                      <Star className="w-3 h-3 fill-amber-300 text-amber-300" />
                      {stats.avg_rating} / 5 média
                    </span>
                  ) : (
                    'Atendidos pela plataforma'
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Trust / Credibility Strip (visible on all breakpoints) */}
      {stats && (stats.active_stores > 0 || stats.total_products > 0 || stats.completed_orders > 0) && (
        <div className="px-4 pb-2" data-testid="trust-strip">
          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm px-4 py-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="text-center">
              <div className="flex items-center justify-center gap-1.5 text-blue-700">
                <Store className="w-4 h-4" />
                <span className="text-2xl font-bold">{stats.active_stores}</span>
              </div>
              <p className="text-[11px] text-slate-500 uppercase tracking-wider mt-0.5">Empresas parceiras</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-1.5 text-blue-700">
                <ShoppingBag className="w-4 h-4" />
                <span className="text-2xl font-bold">{stats.total_products}</span>
              </div>
              <p className="text-[11px] text-slate-500 uppercase tracking-wider mt-0.5">Produtos ativos</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-1.5 text-blue-700">
                <ThumbsUp className="w-4 h-4" />
                <span className="text-2xl font-bold">{stats.completed_orders}</span>
              </div>
              <p className="text-[11px] text-slate-500 uppercase tracking-wider mt-0.5">Pedidos entregues</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-1.5 text-blue-700">
                <Users className="w-4 h-4" />
                <span className="text-2xl font-bold">{stats.unique_customers}</span>
              </div>
              <p className="text-[11px] text-slate-500 uppercase tracking-wider mt-0.5">
                {stats.avg_rating ? (
                  <span className="inline-flex items-center gap-1">
                    Clientes • <Star className="w-3 h-3 fill-amber-400 text-amber-400" /> {stats.avg_rating}
                  </span>
                ) : (
                  'Clientes atendidos'
                )}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="px-4 pb-4">
        <div className="grid grid-cols-4 gap-3">
          <Link to="/novidades" className="quick-action bg-green-500" data-testid="nav-novidades">
            <Sparkles className="w-6 h-6 mb-1" />
            <span>Novidades</span>
            <span className="text-xs opacity-80">Recém chegados</span>
          </Link>
          <Link to="/tendencias" className="quick-action bg-orange-500" data-testid="nav-tendencias">
            <TrendingUp className="w-6 h-6 mb-1" />
            <span>Tendência</span>
            <span className="text-xs opacity-80">Mais vendidos</span>
          </Link>
          <Link to="/lojas" className="quick-action bg-blue-600" data-testid="nav-lojas">
            <Store className="w-6 h-6 mb-1" />
            <span>Lojas</span>
            <span className="text-xs opacity-80">Oficiais</span>
          </Link>
          <Link to="/videos" className="quick-action bg-purple-600" data-testid="nav-videos">
            <Play className="w-6 h-6 mb-1" />
            <span>Vídeos</span>
            <span className="text-xs opacity-80">Destaques</span>
          </Link>
        </div>
      </div>

      {/* Vitrine Dinâmica Automática (Etapa 4) — substitui "Lojas perto de você" */}
      <DynamicShowcase />

      {/* Etapa 3 — Mais bem avaliados */}
      <TopRatedSection limit={10} minReviews={3} />

      {/* Categories */}
      <div className="px-4 py-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-gray-900">Categorias</h2>
          <Link to="/categorias" className="text-blue-600 text-sm font-medium flex items-center gap-1">
            Ver todas <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-4 gap-3">
          {categories.slice(0, 8).map((cat, index) => (
            <Link
              key={cat.id || index}
              to={`/categoria/${cat.id}`}
              className="category-card"
              style={{ backgroundColor: cat.color || '#3b82f6' }}
              data-testid={`category-${cat.id}`}
            >
              <span className="text-2xl mb-1">{cat.icon || '📦'}</span>
              <span className="text-xs leading-tight">{cat.name}</span>
            </Link>
          ))}
        </div>
      </div>

      {/* Featured Products */}
      {data?.featured_products?.length > 0 && (
        <div className="px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-gray-900">Destaques</h2>
            <Link to="/destaques" className="text-blue-600 text-sm font-medium flex items-center gap-1">
              Ver todos <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {data.featured_products.slice(0, 4).map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        </div>
      )}

      {/* New Products */}
      {data?.new_products?.length > 0 && (
        <div className="px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-gray-900">Novidades</h2>
            <Link to="/novidades" className="text-blue-600 text-sm font-medium flex items-center gap-1">
              Ver todos <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {data.new_products.slice(0, 4).map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        </div>
      )}

      {/* Stores with Product Carousels — vitrine principal */}
      {storesFeed.length > 0 && (
        <div className="px-4 py-4 space-y-4" data-testid="stores-feed">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-gray-900">Vitrine das Lojas</h2>
            <Link to="/lojas" className="text-blue-600 text-sm font-medium flex items-center gap-1">
              Ver todas <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          {storesFeed.map(({ store, products }) => (
            <StoreProductCarousel key={store.id} store={store} products={products} />
          ))}
        </div>
      )}

      {/* Stores */}
      {data?.stores?.length > 0 && storesFeed.length === 0 && (
        <div className="px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-gray-900">Lojas Parceiras</h2>
            <Link to="/lojas" className="text-blue-600 text-sm font-medium flex items-center gap-1">
              Ver todas <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="space-y-3">
            {data.stores.slice(0, 3).map((store) => (
              <Link key={store.id} to={`/loja/${store.id}`} className="store-card" data-testid={`store-${store.id}`}>
                <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  {store.logo_url ? (
                    <img src={optimizeCloudinaryUrl(store.logo_url, { width: 200 })} alt={store.name} loading="lazy" className="w-full h-full rounded-xl object-cover" />
                  ) : (
                    <Store className="w-7 h-7 text-blue-600" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900 truncate">{store.name}</h3>
                  <p className="text-sm text-gray-500 flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    {store.city}/{store.state}
                  </p>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400" />
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Share Section */}
      <div className="px-4 py-6">
        <div className="bg-gradient-to-r from-pink-500 to-rose-500 rounded-2xl p-6 text-center text-white">
          <Share2 className="w-10 h-10 mx-auto mb-3" />
          <h3 className="text-xl font-bold mb-2">Compartilhe o DESTACK</h3>
          <p className="text-pink-100 text-sm mb-4">Ajude seus amigos a encontrarem as melhores ofertas!</p>
          <button
            onClick={() => {
              if (navigator.share) {
                navigator.share({
                  title: 'DESTACK VENDAS MAIS',
                  text: 'Confira as melhores ofertas no DESTACK VENDAS MAIS!',
                  url: window.location.href
                });
              } else {
                navigator.clipboard.writeText(window.location.href);
                alert('Link copiado para área de transferência!');
              }
            }}
            className="bg-white text-pink-600 font-semibold px-8 py-3 rounded-full hover:bg-pink-50 transition-colors"
            data-testid="share-section-btn"
          >
            Compartilhar
          </button>
        </div>
      </div>

      {/* Footer */}
      <div className="pb-24">
        <SiteFooter />
      </div>

      {/* Floating Cart */}
      {itemCount > 0 && (
        <Link to="/carrinho" className="floating-cart" data-testid="floating-cart">
          <ShoppingCart className="w-6 h-6" />
          <span className="cart-badge">{itemCount}</span>
        </Link>
      )}

      {/* Bottom Navigation */}
      <BottomNav />

      {/* Side Menu */}
      <SideMenu open={menuOpen} onClose={() => setMenuOpen(false)} user={user} isAdmin={isAdmin} />

      {/* AI Assistant - floating */}
      <AIAssistant />
    </div>
  );
};

// Product Card Component
const ProductCard = ({ product }) => {
  const { addItem } = useCart();
  const navigate = useNavigate();

  const formatPrice = (price) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(price);
  };

  const handleAddToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    addItem(product);
  };

  return (
    <Link to={`/produto/${product.id}`} className="product-card" data-testid={`product-${product.id}`}>
      <div className="relative">
        <img
          src={optimizeCloudinaryUrl(product.images?.[0], { width: 400 }) || 'https://via.placeholder.com/200x200?text=Produto'}
          alt={product.name}
          loading="lazy"
          className="product-card-image"
        />
        {product.promo_price && (
          <span className="absolute top-2 left-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded">
            OFERTA
          </span>
        )}
      </div>
      <div className="p-3">
        <h3 className="font-medium text-gray-900 text-sm truncate">{product.name}</h3>
        <p className="text-xs text-gray-500 truncate">{product.store_name}</p>
        <div className="mt-2">
          {product.promo_price ? (
            <div className="flex items-center gap-2">
              <span className="price-original">{formatPrice(product.price)}</span>
              <span className="price-promo">{formatPrice(product.promo_price)}</span>
            </div>
          ) : (
            <span className="font-bold text-gray-900">{formatPrice(product.price)}</span>
          )}
        </div>
        <button
          onClick={handleAddToCart}
          className="w-full mt-2 bg-blue-600 text-white text-sm font-medium py-2 rounded-lg hover:bg-blue-700 transition-colors"
          data-testid={`add-cart-${product.id}`}
        >
          Adicionar
        </button>
      </div>
    </Link>
  );
};

// Bottom Navigation Component
export const BottomNav = () => {
  const location = window.location.pathname;

  const items = [
    { path: '/', icon: Home, label: 'Início' },
    { path: '/produtos', icon: ShoppingBag, label: 'Produtos' },
    { path: '/videos', icon: Play, label: 'Vídeos' },
    { path: '/lojas', icon: Store, label: 'Lojas' },
    { path: '/excursoes', icon: Truck, label: 'Serviços' },
  ];

  return (
    <nav className="bottom-nav" data-testid="bottom-nav">
      <div className="flex justify-around items-stretch">
        {items.map((item) => {
          const Icon = item.icon;
          const active = location === item.path;
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`bottom-nav-item ${active ? 'active' : ''}`}
              data-testid={`bottom-nav-${item.label.toLowerCase()}`}
              aria-current={active ? 'page' : undefined}
            >
              <span className="nav-indicator" aria-hidden="true" />
              <span className="nav-icon-wrap">
                <Icon strokeWidth={active ? 2.4 : 1.9} className="nav-icon" />
              </span>
              <span className="nav-label">{item.label}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
};

// Side Menu Component
const SideMenu = ({ open, onClose, user, isAdmin }) => {
  const navigate = useNavigate();
  const { logout } = useAuth();

  const handleLogout = () => {
    logout();
    onClose();
    navigate('/');
  };

  return (
    <>
      <div
        className={`fixed inset-0 bg-black/50 z-50 transition-opacity ${open ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        onClick={onClose}
      />
      <div
        className={`fixed top-0 right-0 h-full w-80 bg-white z-50 transform transition-transform ${open ? 'translate-x-0' : 'translate-x-full'}`}
      >
        <div className="p-4 border-b flex items-center justify-between">
          <h2 className="font-bold text-lg">Menu</h2>
          <button onClick={onClose} className="p-2">
            <X className="w-6 h-6" />
          </button>
        </div>
        <div className="p-4 space-y-2">
          {user ? (
            <>
              <div className="p-4 bg-gray-50 rounded-xl mb-4">
                <p className="font-semibold text-gray-900">{user.name}</p>
                <p className="text-sm text-gray-500">{user.email}</p>
              </div>
              <Link to="/meus-pedidos" onClick={onClose} className="block p-3 hover:bg-gray-50 rounded-lg">
                Meus Pedidos
              </Link>
              {isAdmin && (
                <Link to="/admin" onClick={onClose} className="block p-3 hover:bg-gray-50 rounded-lg text-blue-600 font-medium">
                  Painel Admin
                </Link>
              )}
              <button onClick={handleLogout} className="block w-full text-left p-3 hover:bg-gray-50 rounded-lg text-red-600">
                Sair
              </button>
            </>
          ) : (
            <>
              <Link to="/login" onClick={onClose} className="block p-3 bg-blue-600 text-white rounded-lg text-center font-medium">
                Entrar
              </Link>
              <Link to="/cadastro" onClick={onClose} className="block p-3 border border-gray-200 rounded-lg text-center">
                Criar Conta
              </Link>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default MarketplaceHome;
